import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Options } from 'tabulator-tables';

import { cloneDeep, concat, findIndex, forEach, isEmpty, remove } from 'lodash';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import {
  Application,
  ApplicationsService,
  TemporaryScriptSuffix,
  IApplication,
  DialogService,
  IApplicationServers,
  LicensesPageService,
  NotificationService,
  TabulatorService,
  TransformationService,
  UserAuth,
  ValuesExchangeProvider,
  TestListService
} from '@nrwl/common-lib';
import { AddScriptPopupComponent } from '../script-editor/add-script-popup/add-script-popup.component';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { MatDialog } from '@angular/material/dialog';
import { ViewTableComponent } from '@nrwl/view-table';

@Component({
  selector: 'nrwl-applications-details-widget',
  templateUrl: './applications.details.widget.component.html',
  styleUrls: ['./applications.details.widget.component.scss']
})
export class ApplicationsDetailsWidgetComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: Application;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() deleteApplicationEvent: EventEmitter<any> = new EventEmitter();
  //eslint-disable-next-line
  @ViewChild(ViewTableComponent) child: ViewTableComponent;
  scriptData = [];
  dataSource: Application;
  isCloudBased: boolean;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  appImgData: any;
  defaultImgPath: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  listDataSource: any;
  scriptName: string;
  scriptTableObject = {
    column: [],
    data: []
  };
  isActWebEnabled = true;
  displayedScriptColumns = ['name', 'action'];
  //eslint-disable-next-line
  appTestListData: BehaviorSubject<any[]>;
  columnHeader: Array<string>;
  dataIdentity: MatTableDataSource<IApplicationServers>;
  authorized = false;
  isUserManageTest = false;
  appName: string;
  layeredSource: Array<string>;
  tableId = 'application';
  isLoading = false;
  widgetData = {
    id: 'all-monitors',
    type: 'monitor',
    valuesFixed: {
      dimension: 'all-monitors',
      format: 'table'
    },
    valuesDefault: null,
    def: {
      historical: false,
      noHeader: false,
      noBorder: false,
      showLoader: true,
      nonCachable: false,
      managerUrl: '/monitor/MonitorManager/v1/monitor',
      inputsRequired: ['dimension', 'format', 'toTime', 'fromTime'],
      inputsOptional: [],
      actions: {
        bulk: {
          remove: {
            method: 'delete'
          },
          stop: {
            method: 'post',
            path: '/stop'
          },
          start: {
            method: 'post',
            path: '/start'
          }
        },
        item: {
          remove: {
            method: 'delete'
          },
          stop: {
            method: 'post',
            path: '/stop'
          },
          start: {
            method: 'post',
            path: '/start'
          },
          edit: {},
          logs: {}
        }
      },
      outputs: [],
      isActionEnabled: true,
      widgetOptions: {
        defaultSorting: {
          id: 'created',
          start: 'desc'
        }
      },
      widgetType: 'table'
    }
  };
  cacheObj: Subscription;
  hasScript = false;
  table: Options;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private licenseService: LicensesPageService,
    private readonly applicationsService: ApplicationsService,
    private readonly dialogService: DialogService,
    private readonly userAuth: UserAuth,
    private readonly testListService: TestListService,
    private readonly navigationProvider: NavigationProvider,
    private readonly notificationService: NotificationService,
    private readonly exchange: ValuesExchangeProvider,
    private readonly transformService: TransformationService,
    private readonly tabulatorService: TabulatorService,
    public dialog: MatDialog
  ) {
    this.table = this.tabulatorService.commonTableConfiguration();
    this.table.persistenceID = 'application-script-selection';

    this.columnHeader = ['Name', 'Originator', 'Status', 'Test', 'Updated', 'Actions'];
    this.appTestListData = new BehaviorSubject<[]>([]);
  }

  ngOnInit() {
    this.exchange.notifyListeners('new_test', { noValue: true });
    this.exchange.registerInterest('new_test', this.updateCache.bind(this));
    this.exchange.registerInterest('create_script', data => {
      if (data) {
        this.getScripts();
      }
    });
    this.cacheObj = this.testListService.cache.subscribe(data => {
      this.listDataSource = this.testListService.sortDataByName(data);
      this.transformData();
    });
    // TO-DO replace with original image
    this.defaultImgPath = 'default_app_icon';
    this.authorized = this.userAuth.isAuthedFor('READ_WRITE');
    this.isUserManageTest = this.userAuth.isAuthedFor('MANAGE_TESTS');
    this.cacheObj = this.applicationsService.cache
      .pipe(
        takeUntil(this.unsubscribe),
        tap(() => this.transformData())
      )
      .subscribe();
    this.layeredSource = ['Application'];
    //Hiding below code as there is no license check in convergence
    //this.isActWebEnabled = this.licenseService.getIsActWebLicense();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  updateCache(data: any): void {
    if (!data.noValue) {
      this.testListService.updateTestResponse();
    }
  }
  ngOnChanges() {
    this.getScripts();
    this.transformData();
  }
  transformData(): void {
    this.dataSource = cloneDeep(this.data);
    if (this.data) {
      this.appName = this.data?.applicationData.name;
      if (this.appName) {
        const testData = this.listDataSource?.filter(
          item => item?.getDestinationApplicationName() === this.appName
        );
        this.appTestListData.next(testData);
      }

      this.isCloudBased = !isEmpty(this.dataSource.getUrl());
      this.appImgData = this.getApplicationIcon(this.dataSource.getApplications());
    }
  }

  getScripts(): void {
    this.isLoading = true;
    this.applicationsService
      .getScripts$(this.data?.applicationData?.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        res => {
          if (res?.length) {
            this.isLoading = false;
            this.scriptData = res;
            this.assignData();
          }
        },
        () => {
          this.isLoading = false;
          this.scriptData = [];
          this.table.data = [];
        }
      );
  }

  assignData(): void {
    this.scriptData.forEach((data, index) => {
      const className = data.isPreCheckPassed ? 'success' : data.failurecode ? 'failed' : 'none';
      data['id'] = index + 1;
      data['status'] = {
        label: data.isPreCheckPassed ? 'Passed' : data.failurecode ? 'Failed' : 'Not verified',
        sublabel: data.failurecode ? data.errorDescription : '',
        labelClass: className,
        upgradeClass: className
      };
      data['precheckVersionName'] = data.scriptName.includes(TemporaryScriptSuffix)
        ? 'Next version'
        : null;
    });
    const parentChildData = this.scriptData.filter(
      data => !data.scriptName.includes(TemporaryScriptSuffix)
    );
    parentChildData.forEach(data => {
      data['_children'] = this.scriptData.filter(
        item => item.scriptName === data.scriptName + TemporaryScriptSuffix
      );
    });
    const transformedData = this.transformService.transformData(
      'application-script',
      parentChildData
    );
    this.table.minHeight = 180;
    this.table.height = 210;
    this.table.rowHeight = 40;
    this.table.dataTree = true;
    this.table.dataTreeStartExpanded = true;
    this.table.dataTreeSelectPropagate = false;
    this.table.pagination = false;
    this.table.columns = transformedData.column;
    this.table.data = transformedData.data;
    this.table = Object.assign({}, this.table);
  }

  openScriptPopup(editMode: boolean): void {
    if (editMode) {
      this.fetchScriptDetail();
    } else {
      this.dialog.open(AddScriptPopupComponent, {
        width: '100rem',
        disableClose: true,
        data: {
          id: this.data.applicationData.id,
          editMode: false,
          scriptName: '',
          allScriptNames: this.scriptData.map(item => item.scriptName),
          url: this.data.getUrl(),
          isPreCheckPassed: false,
          precheckVersionName: null
        }
      });
    }
  }

  fetchScriptDetail(): void {
    this.applicationsService
      .getScriptDetails$({
        id: this.data.applicationData.id,
        name: encodeURIComponent(this.scriptName)
      })
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(res => {
        this.dialog.open(AddScriptPopupComponent, {
          width: '100rem',
          disableClose: true,
          data: {
            id: this.data.applicationData.id,
            editMode: true,
            scriptName: this.scriptName.trim(),
            scriptDetails: res.scriptContent,
            allScriptNames: this.scriptData.map(item => item.scriptName),
            url: this.data.getUrl(),
            isPreCheckPassed: res.isPreCheckPassed,
            precheckVersionName: this.scriptName.includes(TemporaryScriptSuffix)
              ? this.scriptName.substring(0, this.scriptName.indexOf(TemporaryScriptSuffix)) +
                '(Next version)'
              : null
          }
        });
      });
  }

  addTest(testName: string): void {
    //this.isLoading = true;
    // this.monitorsService
    //   .getMonitorMaxLimit$()
    //   .pipe(takeUntil(this.unsubscribe))
    //   .subscribe(
    //     response => {
    //       if (response) {
    //         this.isLoading = false;
    //         this.monitorsService.setcountResponse(response);
    //         const actualTest =
    //           response.success.monitorMaxLimit - response.success.activeMonitorsCount;
    //         if (actualTest <= 0) {
    //           this.showTestWarningMessage(response.success.monitorMaxLimit);
    //           return;
    //         }
    //         this.navigateToTestSetupPage(testName);
    //       }
    //     },
    //     () => {
    //       this.isLoading = false;
    //       this.navigateToTestSetupPage(testName);
    //     },
    //     () => {}
    //   );
    this.navigateToTestSetupPage(testName);
  }
  navigateToTestSetupPage(testName: string): void {
    this.navigationProvider.navigateToTestSetup(testName, {
      queryParams: {
        defaultParams: JSON.stringify({
          overrides: { selectedAppId: this.data.applicationData.id }
        })
      }
    });
  }

  // showTestWarningMessage(maxLimit: number): void {
  //   const param = this.monitorsService.getCreateTestWarningMsg(maxLimit);
  //   this.dialogService.showDialog('confirm', 'body', param);
  // }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  protocolPortMapping(appServers: any): any {
    const identityDetails = [];
    forEach(appServers, appServer => {
      const protocolName = appServer.ipProtocols[0];
      const portsArray = appServer.portsInfo;
      const identityIndex = findIndex(
        identityDetails,
        details => protocolName === details.protocol
      );
      if (identityIndex !== -1) {
        identityDetails[identityIndex].ports = concat(
          identityDetails[identityIndex].ports,
          portsArray
        );
      } else {
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        let result: any = {};
        result = {
          protocol: protocolName,
          ports: portsArray
        };
        identityDetails.push(result);
      }
    });
    return identityDetails;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getApplicationIcon(dataSource: any): { url: string; icon: string } {
    let iconInfo = null;
    iconInfo = {
      url: dataSource.iconUrl ? dataSource.iconUrl : dataSource.url,
      icon: this.defaultImgPath
    };
    return iconInfo;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getAppServerPorts(appServer: any): any {
    const portArray = [];
    forEach(appServer.ports, port => {
      const portType = port.portValueType;
      const portValue = portType === 'range' ? port.first + '-' + port.last : port.fixed;
      portArray.push(portValue);
    });
    return portArray;
  }

  deleteApplication(appId: number): void {
    const newParams = {
      id: 'application.delete',
      alternateTitle: `Remove application '${this.dataSource.getName()}'?`,
      callBackService: this,
      callBackFnParams: { appId: appId },
      callBackFuntion: this.confirmedDelete
    };
    this.dialogService.showDialog('confirm', 'body', newParams);
  }

  editApplication(appId: number): void {
    let appData;
    if (appId) {
      //editing a dependency of the first-tier app
      appData = this.data.getApplications().appDependencies.find(a => a.id === appId);
    } else {
      //editing the first-tier app
      appData = this.data.getApplications();
    }
    this.applicationsService.navigateToWizard('update_application', appData);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  callBackRemoveFunction(component: any, params: any): void {
    const url = component.updateApplicationURL + '/' + component.dataSource.getId();
    const appDependencies: Array<IApplication> = component.data.getAppDependencies();
    remove(appDependencies, application => application.id === params.appId);
    const appData = component.data.getApplications();
    component._updateApplication$(component, url, appData);
  }

  goToSummaryPage(): void {
    const source = ['Application management'];
    let appSearchName = '';
    if (sessionStorage.getItem('appSearch')) {
      appSearchName = JSON.parse(sessionStorage.getItem('appSearch')).name;
    }
    const appSearchInfo = JSON.stringify({ name: appSearchName, id: this.data.applicationData.id });
    sessionStorage.setItem('appSearch', appSearchInfo);
    this.navigationProvider.navigateToDashboard(['application-summary'], {
      queryParams: {
        appId: this.dataSource.getId(),
        appName: this.dataSource.getName(),
        monitorId: null,
        source,
        layeredSource: null,
        queryParamsHandling: 'merge',
        replaceUrl: true
      }
    });
  }

  ngOnDestroy(): void {
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  onActionClick(event: { id: number; label: string }): void {
    this.scriptName = this.scriptData.find(item => item.id === event.id).scriptName;
    if (event.label === 'Remove') {
      this.removeScript();
    } else if (event.label === 'Edit') {
      this.openScriptPopup(true);
    }
  }

  removeScript(): void {
    const title = this.scriptName.includes(TemporaryScriptSuffix)
      ? this.scriptName.substring(0, this.scriptName.indexOf(TemporaryScriptSuffix)) +
        '(Next version)'
      : this.scriptName;
    const newParams = {
      id: 'script.delete',
      alternateTitle: `Remove script - ${title}`,
      callBackFuntion: this.deleteScript,
      callBackService: this
    };
    this.dialogService.showDialog('confirm', 'body', newParams);
  }

  private confirmedDelete = () => {
    this.deleteApplicationEvent.emit(this.dataSource.getId());
  };
  // eslint-disable-next-line
  private deleteScript(service: any): void {
    service.isLoading = true;
    const scriptNames = [encodeURIComponent(service.scriptName.trim())];
    const hasPrecheckVersion = service.scriptData.some(
      data => data.scriptName === service.scriptName + TemporaryScriptSuffix
    );
    if (hasPrecheckVersion) {
      scriptNames.push(encodeURIComponent(service.scriptName.trim() + TemporaryScriptSuffix));
    }
    const requestObject = {
      id: service.data.applicationData.id,
      scriptNames: { name: scriptNames }
    };
    service.applicationsService
      .deleteBulkScript$(requestObject)
      .pipe(takeUntil(service.unsubscribe))
      .subscribe(
        res => {
          if (res) {
            service.notificationService.onSuccess('Script removed successfully');
            service.getScripts();
          }
        },
        () => {
          service.notificationService.onFail('There is problem while removing a script');
          service.isLoading = false;
        }
      );
  }

  private _updateApplication$(component, url, data): void {
    component.applicationsService
      .updateApplicationData$(url, data)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          const successMsg = 'Application removed successfully';
          component.notificationService.onSuccess(successMsg);
          component.applicationsService.updateApplicationResponse(response);
          this.transformData();
        },
        error => {
          component.notificationService.onFail(error.error.errors[0]);
        },
        () => {}
      );
  }

  //eslint-disable-next-line
  goToTestListPage(): void {
    let appSearchName = '';
    if (sessionStorage.getItem('appSearch')) {
      appSearchName = JSON.parse(sessionStorage.getItem('appSearch')).name;
    }
    const appSearchInfo = JSON.stringify({ name: appSearchName, id: this.data.applicationData.id });
    sessionStorage.setItem('appSearch', appSearchInfo);
    const expression = `device=${this.appName}`;
    this.navigationProvider
      .addRootParam('test_list_filter', expression)
      .navigateToDashboard('test-list-management', {
        queryParams: { layeredSource: ['Application'] }
      });
  }
}
